<template>
    <section id="in-transit-deliveries-page">

        <!-- Modals -->
        <ModalNotes
            v-model="noteData.showModal"
            :notes="noteData.text"
            @addNotes="( text ) => updateNotes( text )"
            @close="noteData.showModal = false"
        />
        <ModalPictureSelection
            v-model="pictureData.showModal"
            :image="pictureData.image"
            :required="isPickupPhotoRequired"
            @clearPicture="clearPictureData()"
            @close="modalPictureCloseHandler()"
            @updatePicture="( data ) => updatePicture( data )"
        />

        <!-- At Destination - Scan Item Input & Destination Name -->
        <v-row
            v-if="hasCurrentDestinationLocatorId"
            class="at-destination-input-row"
            noGutters
        >
            <v-row
                class="input-row"
                noGutters
            >
                <v-text-field
                    ref="inputMailItem"
                    v-model.trim="models.mailItemRefNum"
                    label="Scan Item ID"
                    outlined
                    type="text"
                    @keydown.enter.native="scannedRefNumInputHandler()"
                />
                <ClButton
                    class="input-row__button"
                    color="primary"
                    size="x-large"
                    @click="scannedRefNumInputHandler()"
                >
                    Scan
                </ClButton>
            </v-row>
            <!-- Destination Name -->
            <p
                v-if="!!currentDestination.name"
                class="at-destination-input-row__destination-name"
            >
                {{ currentDestination.name }}
            </p>
        </v-row>
        <!-- Destination Locator Input -->
        <v-row
            v-else
            class="input-row"
            noGutters
        >
            <v-text-field
                ref="inputDestinationLocator"
                v-model.trim="models.destinationLocatorId"
                label="Drop-off Location"
                outlined
                @keydown.enter.native="setCurrentDestination( models.destinationLocatorId )"
            />
            <ClButton
                class="input-row__button"
                color="primary"
                size="x-large"
                @click="setCurrentDestination( models.destinationLocatorId )"
            >
                Set
            </ClButton>
            <v-spacer/>
        </v-row>

        <!-- Tables -->
        <TableDeliverableItems
            v-if="hasCurrentDestinationLocatorId"
        />
        <TableDeliveryItemsByDestination
            v-else
        />

        <!-- Under-table Button Row -->
        <div
            v-if="hasCurrentDestinationLocatorId"
            class="bottom-button-section"
        >
            <div class="bottom-button-section__metadata-buttons">
                <!-- Notes Button -->
                <ClButton
                    :type="noteButtonType"
                    @click="noteData.showModal = true"
                >
                    Notes &nbsp;
                    <v-icon v-if="hasNoteData" small>mdi-message-alert-outline</v-icon>
                </ClButton>
                <!-- Photo Button -->
                <ClButton
                    :color="photoButtonColor"
                    :type="photoButtonType"
                    @click="pictureData.showModal = true"
                >
                    Photo &nbsp;
                    <v-icon v-if="hasPhotoData" small>mdi-message-alert-outline</v-icon>
                </ClButton>
            </div>
            <!-- Done Button -->
            <ClButton
                :disabled="deliveryInFlight"
                :loading="deliveryInFlight"
                :type="doneButtonType"
                class="bottom-button-section__button-done"
                @click="doneButtonHandler()"
            >
                <v-icon left>mdi-check</v-icon>
                Done
            </ClButton>
        </div>

    </section>
</template>

<script>
// Vuex
import { mapGetters, mapState } from 'vuex';

// Utils
import ValidationUtil from '@/utils/ValidationUtil';
import AudioUtil, { soundNames } from '@/utils/AudioUtil';

// Components
import ModalNotes from '@/components/modal/ModalNotes';
import ModalPictureSelection from '@/components/modal/ModalPictureSelection';
import TableDeliverableItems from './outForDelivery/TableDeliverableItems';
import TableDeliveryItemsByDestination from './outForDelivery/TableDeliveryItemsByDestination';

/**
 * Delivery State Enums
 */
const deliveryStates = {
    READY: 'READY',
    ATTEMPTING_TO_DELIVER: 'ATTEMPTING_TO_DELIVER',
};

export default {
    name: 'OutForDelivery',
    components: {
        ModalNotes,
        ModalPictureSelection,
        TableDeliverableItems,
        TableDeliveryItemsByDestination,
    },
    data() {
        return {
            models: {
                destinationLocatorId: '',
                mailItemRefNum: '',
            },
            noteData: {
                showModal: false,
                text: '',
            },
            pictureData: {
                showModal: false,
                image: [],
                hash: [],
            },
            deliveryState: deliveryStates.READY,
        };
    },
    computed: {
        ...mapGetters( {
            websiteId: 'Website/id',
            hasPicklistItems: 'Mail/Delivery/Current/hasPicklistItems',
            isItemInDisplayedDeliverableList: 'Mail/Delivery/Current/isItemInDisplayedDeliverableList',
            getMailItemRefNumFromRefNumOrTrackingNumber: 'Mail/Delivery/Current/getMailItemRefNumFromRefNumOrTrackingNumber',
            hasCurrentDestinationLocatorId: 'Mail/Delivery/Current/hasCurrentDestinationLocatorId',
            getDestinationNameFromPicklistByLocatorId: 'Mail/Delivery/Current/getDestinationNameFromPicklistByLocatorId',
            getMailItemIdListFromScannedItemsForCurrentDestination: 'Mail/Delivery/Current/getMailItemIdListFromScannedItemsForCurrentDestination',
            hasMailItemsToBeDeliveredToCurrentDestination: 'Mail/Delivery/Current/hasMailItemsToBeDeliveredToCurrentDestination',
        } ),
        ...mapState( {
            jwtToken: ( state ) => state.User.jwtToken,
            userAccountId: ( state ) => state.User.accountId,
            getInTransitDeliveriesInFlight: ( state ) => state.Mail.Delivery.inTransit.inFlight,
            deliveryInFlight: ( state ) => state.Mail.Delivery.Current.delivery.inFlight,
            currentDestination: ( state ) => state.Mail.Delivery.Current.delivery.currentDestination,
            currentDestinationLocatorId: ( state ) => state.Mail.Delivery.Current.delivery.currentDestination.locatorId,
            isPickupPhotoRequired: ( state ) => state.Mail.Settings.requirements.pickupPhoto,
        } ),
        /** @returns { Boolean } */
        inFlight() {
            return this.deliveryInFlight || this.getInTransitDeliveriesInFlight;
        },
        /** @returns { Boolean } */
        hasNoteData() {
            return this.noteData
                && this.noteData.text
                && ( this.noteData.text.length > 0 );
        },
        /** @returns { Boolean } */
        hasPhotoData() {
            return !!ValidationUtil.arrayHasItems( this.pictureData.image )
                && !!ValidationUtil.arrayHasItems( this.pictureData.hash );
        },

        /** @returns { String } */
        noteButtonType() {
            return this.hasNoteData
                ? 'primary'
                : 'secondary'; // outlined
        },

        /**
         * Photo is required and we don't have one
         * @returns { Boolean }
         */
        isPhotoButtonColorError() {
            return this.isPickupPhotoRequired && !this.hasPhotoData;
        },
        /** @returns { String } */
        photoButtonColor() {
            return this.isPhotoButtonColorError
                ? 'error'
                : 'primary';
        },
        /** @returns { String } */
        photoButtonType() {
            return this.hasPhotoData
                ? 'primary'
                : 'secondary'; // outlined
        },

        /** @returns { Boolean } */
        isDoneButtonOutlined() {
            return (
                // Photo is required and we don't have one
                this.isPhotoButtonColorError
                // photo isn't required, and doesn't has items
                || ( !this.isPickupPhotoRequired && !this.hasMailItemsToBeDeliveredToCurrentDestination )
                // doesn't have items
                || !this.hasMailItemsToBeDeliveredToCurrentDestination
            );
        },
        /** @returns { String } */
        doneButtonType() {
            return this.isDoneButtonOutlined
                ? 'secondary' // outlined
                : 'primary';
        },
    },
    watch: {
        currentDestinationLocatorId() {
            if ( this.hasCurrentDestinationLocatorId ) {
                // Use nextTick to wait until the ref is available to 'this' component
                this.$nextTick( () => {
                    if ( this.$refs.inputMailItem ) this.$refs.inputMailItem.focus();
                } );
            }
        },
    },
    async mounted() {
        await this.$store.dispatch( 'Mail/Settings/get' );
        this.clearModelDestinationLocatorId();
        if ( !this.hasPicklistItems ) {
            // Navigate away if they got here without items to deliver
            this.$router.push( { path: '/mail/delivery' } );
        } else if ( this.$refs.inputDestinationLocator ) {
            this.$refs.inputDestinationLocator.focus();
        } else if ( this.$refs.inputMailItem ) {
            this.$refs.inputMailItem.focus();
        }
    },
    destroyed() {
        // Clear out local models
        this.clearLocalModels();
        // Clear currentDestination and lists
        this.$store.dispatch( 'Mail/Delivery/Current/reset' );
    },
    methods: {
        continueDelivery() {
            // if they were trying to deliver, and we popped the pictureModal, and they selected a picture
            if ( this.deliveryState === deliveryStates.ATTEMPTING_TO_DELIVER && this.hasPhotoData ) {
                this.doneButtonHandler();
            }
        },
        updateNotes( text ) {
            this.noteData.text = text;
        },
        clearNotesData() {
            this.updateNotes( '' );
        },
        updatePicture( { image, hash } ) {
            this.pictureData.image = image;
            this.pictureData.hash = hash;
            this.pictureData.showModal = false;
            this.continueDelivery();
        },
        clearPictureData() {
            this.pictureData.image = [];
            this.pictureData.hash = [];
        },
        modalPictureCloseHandler() {
            this.pictureData.showModal = false;
            this.continueDelivery();
        },
        clearModelMailItemRefNum() {
            this.models.mailItemRefNum = '';
        },
        clearModelDestinationLocatorId() {
            this.models.destinationLocatorId = '';
        },
        clearLocalModels() {
            this.clearNotesData();
            this.clearPictureData();
            this.clearModelMailItemRefNum();
            this.clearModelDestinationLocatorId();
        },
        async clearLocalModelsAndCurrentDestinationThenReturnToSummary() {
            this.clearLocalModels();
            await this.clearDestinationInfoAndFocusDestinationInput();
        },

        /**
         * Set the currentDestination by locatorId
         * @param { String } locatorId
         */
        async setCurrentDestination( locatorId ) {
            // If there are items to be picked up by this locator ID, go forth!
            // Otherwise, do nothing and play a bad sound.
            const name = this.getDestinationNameFromPicklistByLocatorId( locatorId );
            if ( name ) {
                await AudioUtil.playSound( soundNames.SUCCESS_LONG );
                await this.$store.dispatch( 'Mail/Delivery/Current/setCurrentDestination', {
                    locatorId,
                    name,
                } );
                this.clearModelDestinationLocatorId();
            } else {
                await AudioUtil.playSound( soundNames.FAILURE_LONG );
                await this.$store.dispatch( 'snackbar', {
                    message: `"${ locatorId }" has no deliverable items.`,
                    type: 'error',
                    duration: 5000,
                }, { root: true } );
            }
        },

        /**
         * Clear out currentDestination info locally and in state
         * Then refocus inputDestinationLocator
         */
        async clearDestinationInfoAndFocusDestinationInput() {
            // Clear the currentDestination
            this.clearModelDestinationLocatorId();
            await this.$store.dispatch( 'Mail/Delivery/Current/clearCurrentDestination' );
            // Focus the destinationLocator input
            this.$nextTick( () => {
                if ( this.$refs.inputDestinationLocator ) this.$refs.inputDestinationLocator.focus();
            } );
        },

        scannedRefNumInputHandler() {
            if ( this.isItemInDisplayedDeliverableList( this.models.mailItemRefNum ) ) {
                // Add the item to our list of scanned items
                this.$store.dispatch( 'Mail/Delivery/Current/addScannedItem', {
                    item: this.getMailItemRefNumFromRefNumOrTrackingNumber( this.models.mailItemRefNum ),
                    destinationLocatorId: this.currentDestinationLocatorId,
                } );
                AudioUtil.playSound( soundNames.SUCCESS_SHORT );
            } else {
                AudioUtil.playSound( soundNames.FAILURE_SHORT );
                this.$store.dispatch( 'snackbar', {
                    message: 'Scanned Item is NOT in the current Delivery List',
                    type: 'error',
                    duration: 5000,
                    onAction: () => {
                        // Clear our scanned item input
                        this.clearModelMailItemRefNum();
                        // Focus the input again
                        this.$refs.inputMailItem.focus();
                    },
                } );
            }
            // Clear our scanned item input
            this.clearModelMailItemRefNum();
            // Focus the input again
            this.$refs.inputMailItem.focus();
        },
        async doneButtonHandler() {
            this.deliveryState = deliveryStates.READY;

            // no items scanned
            if ( !this.hasMailItemsToBeDeliveredToCurrentDestination ) {
                // has photo attached, pop confirmation
                if ( this.hasPhotoData ) {
                    await AudioUtil.playSound( soundNames.FAILURE_SHORT );
                    this.$store.dispatch( 'dialog', {
                        title: 'Cancel Delivery',
                        message: "You've attached a Photo, but not scanned any items to deliver. Would you like to cancel this delivery?",
                        cancelText: 'Nevermind',
                        confirmText: 'Yes, Cancel Delivery',
                        width: 500,
                        onConfirm: () => {
                            this.clearLocalModelsAndCurrentDestinationThenReturnToSummary();
                        },
                    } );
                } else {
                    // no items scanned, no photo, go to summary
                    await this.clearLocalModelsAndCurrentDestinationThenReturnToSummary();
                }
            } else { // has items scanned
                // pickup photo is required, pop up modal
                if ( this.isPickupPhotoRequired && !this.hasPhotoData ) {
                    this.deliveryState = deliveryStates.ATTEMPTING_TO_DELIVER;
                    this.pictureData.showModal = true;
                    return; // pop modal and bail out
                }
                // we have scanned items, we have a photo if it is required, deliver the items
                await this.deliverScannedItemsToDestination();
                await this.clearLocalModelsAndCurrentDestinationThenReturnToSummary();
            }
        },

        async deliverScannedItemsToDestination() {
            try {
                await this.$store.dispatch( 'Mail/Delivery/Current/deliverMailItem', {
                    mailItemIdList: this.getMailItemIdListFromScannedItemsForCurrentDestination,
                    locatorId: this.currentDestinationLocatorId,
                    notes: this.noteData.text,
                    photos: this.hasPhotoData
                        ? [ {
                            checkSum: this.pictureData.hash[ 0 ].toString(),
                            data: this.pictureData.image[ 0 ].toString(),
                        } ]
                        : [],
                } );
                // Go fetch a new list of things to deliver
                await this.$store.dispatch( 'Mail/Delivery/getInTransitDeliveries' );
                this.$store.dispatch( 'snackbar', {
                    duration: 5000,
                    message: 'Delivery Completed!',
                    type: 'success',
                } );
            } catch ( error ) {
                this.$store.dispatch( 'snackbar', {
                    message: ( error.response && error.response.data && error.response.data.message )
                        ? error.response.data.message
                        : error.message,
                    type: 'error',
                    duration: 5000,
                } );
            }
        },
    },
};
</script>

<style lang="scss" scoped>
#in-transit-deliveries-page {
    display: flex;
    flex-flow: column nowrap;
}

.input-row {
    &__button {
        margin-left: 1rem;
        font-size: 1.25rem;
    }
}

.at-destination-input-row {
    display: flex;
    flex-flow: column-reverse nowrap;

    &__destination-name {
        font-size: 2rem;
        color: grey;
    }
}

.bottom-button-section {
    margin-top: 2rem;
    display: flex;
    flex-flow: column nowrap;

    &__metadata-buttons {
        display: flex;
        justify-content: space-between;

        .v-btn {
            width: 45%;
        }
    }

    &__button-done {
        margin-top: 2rem;
    }
}

@include mixins-above(tablet) {
    #in-transit-deliveries-page {
        margin-top: 2rem;
    }

    .at-destination-input-row {
        display: flex;
        flex-flow: row nowrap;
        justify-content: space-between;

        .input-row {
            max-width: 40%;
            flex-flow: row nowrap;
        }

        &__destination-name {
            margin-right: 0;
            margin-left: auto;
        }
    }

    .bottom-button-section {
        flex-flow: row nowrap;

        &__button-done {
            margin-top: 0;
            margin-right: 0;
            margin-left: auto;
        }
    }
}
</style>
